import React, { useEffect } from 'react';
import { Box, Button, Grid, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DialogBase from '../../Shared/DialogBase';
import BuildingsList from '../Shared/BuildingsList';
import { AddRequestDTO, AddRequestEmployeeMobileRequest, MobileBuildingDTO } from '../../../ApiOld/Api/ApiServerVov';
import EmployeeList from '../Shared/EmployeeList';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { VovClient } from '../../../ApiOld/ApiServerVov';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import i18n from '../../../Localization/i18nInitializer';
import QuestionDialog from '../../Shared/QuestionDialog';


interface MobileRequestCreateDialogProps {
    open: boolean;
    onClose: (needRefresh: boolean) => void;
}

export default function MobileRequestCreateDialog(props: Readonly<MobileRequestCreateDialogProps>) {
    const { t } = useTranslation();
    const { open, onClose } = props;
    const [buildingId, setBuildingId] = React.useState<number>();
    const [newTask, setNewTask] = React.useState<AddRequestDTO>(new AddRequestDTO());

    const [closeDialog, setCloseDialog] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);

    const { authorizedMobileEmployeeApi } = useMobileApi(VovClient);

    useEffect(() => {
        if (!open) {
            setBuildingId(undefined);
            setNewTask(new AddRequestDTO({ datetimeTo: undefined }));
        }
    }, [open]);

    useEffect(() => {
        if (newTask) {
            setNewTask(new AddRequestDTO({ ...newTask, buildingId: buildingId }));
        }
    }, [buildingId]);

    return (
        <DialogBase fullScreen showCloseButton title={t('createNewTask')} open={open} onClose={() => setCloseDialog(true)}>
            {!buildingId && <Grid container>
                <Grid item xs={12}>
                    <BuildingsList onBuildingClick={(building: MobileBuildingDTO | undefined) => {
                        setBuildingId(building?.buildingId);
                    }} />
                </Grid>
            </Grid>
            }
            {buildingId && <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        value={newTask.text}
                        label={`${t('singleTodo.taskName')}*`}
                        onChange={(e) => {
                            setNewTask(new AddRequestDTO({ ...newTask, text: e.currentTarget.value }));
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        multiline
                        fullWidth
                        size="small"
                        value={newTask.description}
                        label={t('singleTodo.yourMessage')}
                        onChange={(e) => {
                            setNewTask(new AddRequestDTO({ ...newTask, description: e.currentTarget.value }));
                        }}
                        InputProps={{
                            inputProps: {
                                style: {
                                    minHeight: '2rem', // Ensures at least 2 rows (approximate height)
                                    maxHeight: '300px', // Limits the growth to 300px
                                    overflow: 'auto', // Adds scrollbars after reaching maxHeight
                                    padding: '4px'
                                },
                            },
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                resize: 'none', // Prevents manual resizing by the user
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <EmployeeList
                        isMandatory
                        buildingId={buildingId}
                        onEmployeeClick={(emp) =>
                            setNewTask(new AddRequestDTO({ ...newTask, adminId: emp?.adminId }))
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label={t('Deadline')}
                            inputFormat="DD.MM.YYYY HH:mm"
                            ampm={false}
                            value={newTask.datetimeTo ?? null}
                            onChange={(e, v) => {
                                setNewTask(new AddRequestDTO({ ...newTask, datetimeTo: e ?? undefined }));
                            }}
                            renderInput={(params) => (
                                <TextField size="small" {...params} sx={{ width: '100%' }} />
                            )}
                        />
                    </LocalizationProvider>
                </Grid>

                <Box display={'flex'} justifyContent={'space-between'} width={'100%'}
                    sx={{
                        position: 'fixed', // Fixed to the viewport
                        bottom: '16px', // Distance from the bottom
                        zIndex: 1000, // Ensure it floats above other content
                    }}
                >
                    <div />
                    <Button
                        variant="contained"
                        disabled={saving}
                        sx={{ minWidth: '120px' }}
                        onClick={() => { setCloseDialog(true); }}
                    >
                        {t('close')}
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ minWidth: '120px' }}
                        disabled={!newTask.text || !newTask.adminId || saving}
                        onClick={() => {
                            setSaving(true);
                            authorizedMobileEmployeeApi
                                .addRequestEmployeeMobile(
                                    new AddRequestEmployeeMobileRequest({ request: new AddRequestDTO({ ...newTask, text: newTask.text ?? '', description: newTask.description ?? '', images: [] }) })
                                )
                                .then(() => {
                                    onClose(true);
                                })
                                .catch(() => {
                                    onClose(false);
                                }).finally(() => {
                                    setSaving(false);
                                });
                        }}
                    >
                        {t('save')}
                    </Button>
                    <div />
                </Box>
            </Grid>
            }
            <QuestionDialog onClose={(res) => {
                if (res) {
                    onClose(false);
                }
                setCloseDialog(false);
            }} text={t('taskWillBeDeletedReallyContinue')} title={t('reallyClose')} open={closeDialog} />
        </DialogBase>

    )
}
